import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import type { MeResponse } from "@/lib/api";

type AreaEntry = MeResponse["areas"][number];

/**
 * The left rail — the areas, one accordion level deep to their modules.
 *
 * Two rules this component enforces rather than merely follows:
 *
 * * **Unentitled areas are ABSENT.** They are not passed in and there is no
 *   disabled branch to render them. A greyed-out row tells someone what exists
 *   and that they cannot have it, which is a disclosure (AC-2, BR-3).
 * * **Order comes from the server.** `rail_order` is set in `areas.yaml`, the
 *   naming SSOT; this component never sorts and never names an area itself
 *   (I-5, seam S10).
 *
 * ## The three levels (approved design, canvas 2026-09-04-one-os-portal-area-rail)
 *
 * An area, a module and a tab must each be identifiable WITHOUT reading the
 * text, and each pair must differ by more than one cue — a chevron alone is not
 * hierarchy, so here the chevron is decoration and every level survives its
 * deletion:
 *
 * | Level  | Treatment                                                        |
 * |--------|------------------------------------------------------------------|
 * | Area   | 13px bold UPPERCASE, 0.06em tracking, no indent, 32px tall        |
 * | Module | 14px regular sentence case, 24px indent, 34px tall, 6px radius    |
 * | Tab    | horizontal, on white, underlined when active (`ModuleTabs`)       |
 *
 * **The area label is never smaller than a module.** The canvas edition set
 * areas at 11px, the Linear/Notion "small caps" convention; on the live rail
 * that read as the section being subordinate to its own items (Damien,
 * 2026-09-05). 13px bold uppercase keeps the label a label — weight and case
 * still carry the level — while the parent is at least the size of the child.
 *
 * **Selection is inverted, not washed.** The active module is a solid
 * `ks-purple` pill with white bold text (9.6:1 — AAA at any size); its parent
 * area label turns `ks-purple` (9.0:1) and a `ks-purple-3` thread runs down to
 * it, which is the "parent of the active module" cue. Linear and Notion mark
 * selection with a faint grey wash, tuned for someone living in the tool all
 * day; six people open this portal occasionally, so recall beats calm. Nothing
 * depends on colour alone: selected also carries weight and a filled shape,
 * focus carries a 2px ring, and hover LIFTS a row to pure white against the
 * rail tint rather than tinting further into it — transparent → white → purple,
 * three steps that never collide.
 *
 * **Target size — a deliberate step from AAA to AA.** Rows were `min-h-11`
 * (44px, WCAG 2.2 target-size AAA). The approved design takes them to 34px
 * (module) and 32px (area) so every area plus every module fits a 13-inch
 * viewport without the rail becoming a scroll region for its own sake — still
 * over the 24px AA minimum, and both rows stay full-width so the horizontal
 * target is unchanged. The rest of the app keeps `min-h-11`.
 */
export function AreaRail({
  areas,
  openAreaKey,
  activeModuleKey,
  onOpenArea,
  onOpenModule,
}: {
  areas: AreaEntry[];
  openAreaKey: string | null;
  activeModuleKey: string | null;
  onOpenArea: (areaKey: string) => void;
  onOpenModule: (areaKey: string, moduleKey: string) => void;
}) {
  return (
    <nav
      id="area-rail"
      aria-label="Areas"
      className="h-full w-56 shrink-0 border-r border-ks-grey-4 bg-ks-purple-4 px-2 py-3"
    >
      <Accordion
        type="single"
        collapsible
        value={openAreaKey ?? ""}
        onValueChange={(v) => v && onOpenArea(v)}
      >
        {areas.map((area) => {
          // The area that PARENTS the selection, which is not the same thing as
          // the open one: someone can open a second area to look around without
          // the rail forgetting where they actually are.
          const parentsSelection =
            activeModuleKey !== null &&
            area.modules.some((m) => m.module_key === activeModuleKey);

          return (
            <AccordionItem key={area.area_key} value={area.area_key} className="border-none">
              <AccordionTrigger
                className={
                  "h-8 items-center rounded-ks-sm px-2.5 py-0 text-left text-[13px] font-bold" +
                  " tracking-[0.06em] uppercase hover:no-underline" +
                  " **:data-[slot=accordion-trigger-icon]:size-3 " +
                  (parentsSelection
                    ? "text-ks-purple **:data-[slot=accordion-trigger-icon]:text-ks-purple"
                    : "text-ks-ink **:data-[slot=accordion-trigger-icon]:text-ks-grey-2")
                }
              >
                {area.display_name}
              </AccordionTrigger>
              <AccordionContent className="pb-2">
                {area.modules_status === "pending" ? (
                  // UX S2 partial. The rest of the rail stays usable; this one
                  // area says it is still resolving rather than claiming empty.
                  <div className="px-3 py-2" aria-live="polite">
                    <Skeleton className="h-4 w-32" />
                    <span className="sr-only">Loading modules in {area.display_name}</span>
                  </div>
                ) : area.modules.length === 0 && !area.placeholders?.length ? (
                  // UX S3 empty. The area owner is named on the landing screen,
                  // where there is room for the sentence; the rail stays terse.
                  <p className="px-3 py-2 text-sm text-ks-ink">No modules yet</p>
                ) : (
                  <ul className="relative pl-6">
                    {parentsSelection && (
                      // The thread — the only cue that ties the purple area
                      // label to the purple pill several rows below it.
                      <span
                        aria-hidden="true"
                        className="absolute top-0 bottom-1 left-3.5 w-0.5 rounded-full bg-ks-purple-3"
                      />
                    )}
                    {area.modules.map((module) => {
                      const active = module.module_key === activeModuleKey;
                      return (
                        <li key={module.module_key}>
                          <button
                            type="button"
                            onClick={() => onOpenModule(area.area_key, module.module_key)}
                            aria-current={active ? "page" : undefined}
                            className={
                              "flex h-[34px] w-full items-center rounded-ks-sm px-2.5 text-left" +
                              " text-sm outline-none focus-visible:outline-2" +
                              " focus-visible:outline-offset-2 focus-visible:outline-ks-purple " +
                              (active
                                ? "bg-ks-purple font-bold text-ks-surface"
                                : "font-normal text-ks-ink hover:bg-ks-surface hover:shadow-sm")
                            }
                          >
                            {module.display_name}
                          </button>
                        </li>
                      );
                    })}
                    {area.placeholders?.map((name) => (
                      // Coming soon: a name from areas.yaml, not a module. No
                      // button and no route — it cannot be opened or focused.
                      // The name stays ink (grey-1 on the rail tint is ~3:1);
                      // the badge and the missing hover carry "not yet".
                      <li
                        key={`placeholder:${name}`}
                        className="flex h-[34px] items-center justify-between px-2.5 text-sm text-ks-ink"
                      >
                        <span>{name}</span>
                        <span className="rounded-full bg-ks-grey-4 px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-ks-ink uppercase">
                          Soon
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </nav>
  );
}

/** The rail's loading state, at final dimensions so nothing reflows (UX S2 loading). */
export function AreaRailSkeleton() {
  return (
    <nav
      id="area-rail"
      aria-label="Areas"
      className="h-full w-56 shrink-0 border-r border-ks-grey-4 bg-ks-purple-4 px-2 py-3"
    >
      {Array.from({ length: 9 }, (_, i) => (
        <div key={i} className="flex h-8 items-center px-2.5">
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
      <span className="sr-only">Loading your areas</span>
    </nav>
  );
}
